import { useEffect, useRef, useState, type ChangeEvent } from "react"
import { ArrowRight, Loader2, ShieldCheck, ShoppingCart, Trash2 } from "lucide-react"
import { useCart } from "../cart"
import { formatPrice, kindLabel } from "../lib/format"
import { startCheckout, startGuestCheckout, startGuestInvoice } from "../api"
import { CONFIG } from "../config"
import type { PaymentMethod } from "../types"

interface Props {
  open: boolean
  authed: boolean
  onClose: () => void
  onLogin: () => void
}

/** Auswahlkarte fuer die Zahlungsart. */
function PayOption({
  checked,
  onSelect,
  title,
  children,
}: {
  checked: boolean
  onSelect: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={`flex w-full gap-3 rounded-field border p-4 text-left transition ${
        checked ? "border-brand-blue bg-brand-tint" : "border-line hover:border-brand-blue"
      }`}
    >
      <span
        className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          checked ? "border-brand-blue" : "border-line"
        }`}
      >
        {checked && <span className="h-2 w-2 rounded-full bg-brand-blue" />}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-medium">{title}</span>
        <span className="mt-1 block text-sm text-brand-muted">{children}</span>
      </span>
    </button>
  )
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  className = "",
}: {
  label: string
  value: string
  onChange: (e: ChangeEvent<HTMLInputElement>) => void
  type?: string
  className?: string
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block p-1 text-sm font-medium">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        className="h-10 w-full rounded-field border border-line px-3 text-sm outline-none focus:border-brand-blue focus:ring-1 focus:ring-brand-blue"
      />
    </label>
  )
}

export function CartDrawer({ open, authed, onClose, onLogin }: Props) {
  const { lines, subtotalNet, remove, clear } = useCart()
  const [method, setMethod] = useState<PaymentMethod>("checkout")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invoiceDone, setInvoiceDone] = useState(false)
  // Zustimmungen (Pflicht vor Kauf).
  const [privacyOk, setPrivacyOk] = useState(false)
  const [usageOk, setUsageOk] = useState(false)
  const consentOk = privacyOk && usageOk
  // Gast-Zahlweg + Rechnungsanfrage (Neukunde ohne Konto).
  const [guestPay, setGuestPay] = useState<"card" | "invoice">("card")
  const [invoiceRequested, setInvoiceRequested] = useState(false)
  const [bill, setBill] = useState({ email: "", company: "", street: "", postal_code: "", city: "", country: "DE", ust_id: "", note: "" })
  const setBillField = (k: keyof typeof bill) => (e: ChangeEvent<HTMLInputElement>) =>
    setBill((b) => ({ ...b, [k]: e.target.value }))

  const closeRef = useRef<HTMLButtonElement>(null)

  // ESC schliesst; beim Oeffnen Fokus auf den Schliessen-Button.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose()
    window.addEventListener("keydown", onKey)
    closeRef.current?.focus()
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onClose])

  async function checkout() {
    setError(null)
    if (lines.length === 0) return
    if (!consentOk) {
      setError("Bitte bestätigen Sie die beiden Hinweise, um fortzufahren.")
      return
    }
    setBusy(true)
    try {
      // Gast + "Auf Rechnung": Rechnungsanfrage. KEIN Konto/Zugang, bis die
      // Buchhaltung die Rechnung im Admin freigibt (finalize+send).
      if (!authed && guestPay === "invoice") {
        if (!bill.email || !bill.company || !bill.street || !bill.postal_code || !bill.city) {
          setError("Bitte E-Mail, Firma/Name und die Rechnungsadresse vollständig angeben.")
          return
        }
        await startGuestInvoice(lines, bill)
        setInvoiceRequested(true)
        clear()
        return
      }
      // Gast (Karte): Gast-Checkout; Konto entsteht nach der Zahlung.
      // Eingeloggter Kunde: regulaerer Checkout (Karte ODER Rechnung).
      const res = authed ? await startCheckout(lines, method) : await startGuestCheckout(lines)
      if (res.mode === "checkout" && res.url) {
        window.location.href = res.url // Weiter zu Stripe Checkout
        return
      }
      if (res.mode === "invoice") {
        setInvoiceDone(true) // Rechnung ausgeloest, Freischaltung via Webhook
        clear() // Warenkorb nach erfolgreichem Kauf leeren
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unbekannter Fehler.")
    } finally {
      setBusy(false)
    }
  }

  const done = invoiceDone || invoiceRequested
  const showCheckout = !done && lines.length > 0

  return (
    <div className={`fixed inset-0 z-30 transition ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      <div
        className={`absolute inset-0 bg-[rgba(15,18,20,0.45)] transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Warenkorb"
        className={`absolute right-0 top-0 flex h-full w-full max-w-[448px] flex-col rounded-l-card bg-white shadow-panel transition-transform ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between gap-4 border-b border-line px-6 py-6">
          <span className="flex items-center gap-3 text-base font-medium text-brand-blue">
            <ShoppingCart className="h-5 w-5" />
            Warenkorb
          </span>
          <button
            ref={closeRef}
            onClick={onClose}
            aria-label="Schließen"
            className="rounded-full p-1 text-brand-muted hover:bg-surface"
          >
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {invoiceRequested ? (
            <div className="rounded-card bg-surface p-4 text-sm">
              <p className="font-medium">Rechnungsanfrage übermittelt.</p>
              <p className="mt-1 text-brand-muted">
                Ihre Anfrage liegt bei unserer Buchhaltung. Nach Prüfung senden wir Ihnen die Rechnung
                (zahlbar in 14 Tagen); mit dem Rechnungsversand wird Ihr Zugang freigeschaltet und Sie
                erhalten Ihre Zugangsdaten per E-Mail. Sie können dieses Fenster schließen.
              </p>
            </div>
          ) : invoiceDone ? (
            <div className="rounded-card bg-surface p-4 text-sm">
              <p className="font-medium">Rechnung ausgelöst.</p>
              <p className="mt-1 text-brand-muted">
                Ihr Zugang wird sofort freigeschaltet, die Rechnung kommt per E-Mail (zahlbar in 14
                Tagen). Sie können dieses Fenster schließen.
              </p>
            </div>
          ) : lines.length === 0 ? (
            <p className="py-10 text-center text-sm text-brand-muted">Ihr Warenkorb ist leer.</p>
          ) : (
            <>
              <ul>
                {lines.map((l) => (
                  <li key={l.item.price_id} className="flex items-start gap-3 border-b border-line py-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-brand-muted">{kindLabel(l.item)}</div>
                      <div className="text-sm text-brand-card">{l.item.name}</div>
                      {l.selectedPackages && l.selectedPackages.length > 0 && (
                        <div className="mt-1 text-xs text-brand-muted">
                          Gewählt: {l.selectedPackages.map((p) => p.name).join(", ")}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={() => remove(l.item.price_id)}
                        aria-label="Position entfernen"
                        className="rounded-full p-1 text-brand-muted hover:bg-surface"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      <span className="whitespace-nowrap text-sm text-brand-card">
                        {formatPrice(l.item.amount_net, l.item.currency)}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-5 flex justify-between gap-3 text-sm font-medium text-brand-blue">
                <span>Zwischensumme (netto)</span>
                <span>{formatPrice(subtotalNet)}</span>
              </div>
              <p className="mt-1 text-sm text-brand-muted">
                Zzgl. gesetzlicher USt. Die Umsatzsteuer wird im Checkout berechnet (inkl.
                Reverse-Charge bei USt-ID).
              </p>

              <div className="mt-5 flex flex-col gap-4" role="radiogroup" aria-label="Zahlungsart">
                {authed ? (
                  <>
                    <PayOption checked={method === "checkout"} onSelect={() => setMethod("checkout")} title="Karte, Apple Pay, Google Pay">
                      Sofortige Freischaltung nach Zahlung.
                    </PayOption>
                    <PayOption checked={method === "invoice"} onSelect={() => setMethod("invoice")} title="Rechnung / Überweisung">
                      Freischaltung sofort, Rechnung per E-Mail, zahlbar in 14 Tagen.
                    </PayOption>
                  </>
                ) : (
                  <>
                    <PayOption checked={guestPay === "card"} onSelect={() => setGuestPay("card")} title="Karte, Apple Pay, Google Pay">
                      Sofortige Freischaltung nach Zahlung. Konto wird automatisch angelegt
                      (Onboarding per E-Mail).
                    </PayOption>
                    <PayOption checked={guestPay === "invoice"} onSelect={() => setGuestPay("invoice")} title="Auf Rechnung (Neukunde)">
                      Wir prüfen Ihre Anfrage und senden die Rechnung (zahlbar in 14 Tagen). Ihr
                      Zugang wird nach dem Rechnungsversand freigeschaltet.
                    </PayOption>
                  </>
                )}
              </div>

              {!authed && guestPay === "invoice" && (
                <div className="mt-4 flex flex-col gap-3">
                  <Field label="Name / Firma *" value={bill.company} onChange={setBillField("company")} />
                  <Field label="E-Mail *" type="email" value={bill.email} onChange={setBillField("email")} />
                  <Field label="Straße und Hausnummer *" value={bill.street} onChange={setBillField("street")} />
                  <div className="flex gap-4">
                    <Field label="Postleitzahl *" value={bill.postal_code} onChange={setBillField("postal_code")} className="w-[120px] shrink-0" />
                    <Field label="Ort *" value={bill.city} onChange={setBillField("city")} className="flex-1" />
                  </div>
                  <Field label="USt-IdNr. (Reverse-Charge)" value={bill.ust_id} onChange={setBillField("ust_id")} />
                  <Field label="Bemerkung" value={bill.note} onChange={setBillField("note")} />
                  <p className="text-sm text-brand-muted">
                    Es entsteht noch kein Konto. Nach Freigabe durch unsere Buchhaltung erhalten Sie
                    Ihre Zugangsdaten per E-Mail.
                  </p>
                </div>
              )}

              {/* Pflicht-Zustimmungen vor dem Kauf. */}
              <label className="mt-5 flex cursor-pointer gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-brand-blue"
                  checked={privacyOk}
                  onChange={(e) => setPrivacyOk(e.target.checked)}
                />
                <span className="text-sm">
                  <span className="block font-medium">Datenschutz</span>
                  <span className="mt-1 block text-brand-muted">
                    Ich habe die{" "}
                    <a href={CONFIG.datenschutzUrl} target="_blank" rel="noreferrer" className="font-medium text-brand-blue hover:underline">
                      Datenschutzerklärung
                    </a>{" "}
                    gelesen und willige in die Verarbeitung meiner angegebenen Daten ein.
                  </span>
                </span>
              </label>

              <label className="mt-5 flex cursor-pointer gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-brand-blue"
                  checked={usageOk}
                  onChange={(e) => setUsageOk(e.target.checked)}
                />
                <span className="text-sm">
                  <span className="block font-medium">Nutzung &amp; KI-Verarbeitung</span>
                  <span className="mt-1 block text-brand-muted">
                    Ich verpflichte mich, die über den SINUS Milieu-Navigator und die bezogenen
                    Infopakete bereitgestellten Inhalte, Daten und Auswertungen ausschließlich
                    innerhalb des lizenzierten Navigators bzw. für interne Zwecke zu nutzen. Ich
                    werde diese Inhalte – auch in Teilen – weder veröffentlichen, vervielfältigen
                    noch an Dritte weitergeben und insbesondere nicht mittels externer
                    KI-Anwendungen (z. B. ChatGPT, Gemini o. ä.) weiterverarbeiten oder zum Training
                    solcher Systeme verwenden.
                  </span>
                </span>
              </label>

              <div className="mt-5 rounded-card bg-surface p-4">
                <div className="flex items-center justify-between text-sm font-medium">
                  Datenschutz
                  <ShieldCheck className="h-4 w-4 text-brand-muted" />
                </div>
                <p className="mt-2 text-sm text-brand/80">
                  Verarbeitung durch uns: Ihre Eingaben und hochgeladenen Dokumente werden
                  ausschließlich über AWS Bedrock innerhalb der EU verarbeitet, nicht an Dritte
                  weitergegeben und nicht zum Training von KI-Modellen verwendet. Details in der{" "}
                  <a href={CONFIG.datenschutzUrl} target="_blank" rel="noreferrer" className="underline">
                    Datenschutzerklärung
                  </a>
                  .
                </p>
              </div>
            </>
          )}
        </div>

        {showCheckout && (
          <div className="flex flex-col gap-2 border-t border-line bg-white px-6 py-4">
            {error && <p className="text-sm text-brand-accent">{error}</p>}
            <button
              onClick={checkout}
              disabled={busy || !consentOk}
              className="flex w-full items-center justify-center gap-2 rounded-field bg-brand-blue px-6 py-2.5 text-sm font-medium text-white transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-45"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {!authed && guestPay === "invoice" ? "Rechnungsanfrage senden" : "Kostenpflichtig kaufen"}
            </button>
            {!authed && (
              <button
                type="button"
                onClick={onLogin}
                className="w-full rounded-field bg-surface px-6 py-2.5 text-sm font-medium transition hover:bg-line"
              >
                Bereits Kunde? Hier anmelden
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  )
}
